import { AxiosError, AxiosResponse } from 'axios';
import { useAccountStore } from '#src/store/account';
import { showChangeActionDialog } from 'services/notification';
import _sortBy from 'lodash/sortBy';
import axios from 'axios';
import Bugsnag from '@bugsnag/js';
import QueryString from 'qs';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _omitBy from 'lodash/omitBy';
import { defineStore, storeToRefs } from 'pinia';
import { Dialog } from 'quasar';
import { api, getAll } from 'services/api';
import { useOrderStore } from '#src/store/order';
import { useDayjs } from 'composables/dayjs';
import useLogger from 'composables/logger';
import Device from '@models/Device';
import { JobExecutionSummaryForThing, ThingDocument } from '@aws-sdk/client-iot';
import { ravenToSettings, settingsToRaven } from '#server/shared/converter';
import { STATUSES, validStatuses } from '#server/middleware/status-validators';
import BulkExecutionDialog from 'components/bulk-update/BulkExecutionDialog.vue';
import { useHistoryStore } from '#src/store/history';

export enum DEVICE_TYPE {
  ASSET = 'ASSET',
  MUNIC = 'MUNIC',
  OTHER = 'OTHER',
  RAVEN = 'RAVEN',
}

export const SUPPORTED_SIM_SOURCES = Object.freeze(['twilio', 'kore']);

export const DEVICE_MODEL_ALIASES: { [modelName: string]: string } = Object.freeze({
  GL300: 'GL300',
  GL300A: 'GL300',
  GL500: 'GL500',
  GL700: 'GL700',
  Munin: 'Munin',
  Simulator: 'Simulator',
  XT2469A31100281T: 'XT2469',
  XT2469A32100281T: 'XT2469',
  XT2469A311002A1T: 'XT2469 w/battery',
  XT2469A321002A1T: 'XT2469 w/battery',
  XT6300: 'XT6376',
  XT6376A: 'XT6376',
});

export const useDeviceStore = defineStore({
  id: 'device',
  state: () => ({
    accountDeviceErrors: {} as { [accountKey: string]: string },
    accountDeviceRequests: {} as { [accountKey: string]: Promise<void> },
    applyingConfigs: {} as { [serial: string]: boolean },
    commandJobsLoading: {} as { [serial: string]: boolean },
    configsLoading: {} as { [serial: string]: boolean },
    configStatusDeltas: {} as { [serial: string]: IotDeviceConfigurationStatusDelta | null },
    configStatuses: {} as { [serial: string]: DeviceConfigStatus },
    connectivityLoading: {} as { [serial: string]: boolean },
    connectivityStatuses: {} as { [serial: string]: DeviceConnectivityStatus },
    deviceCommands: {} as { [serial: string]: string[] },
    deviceJobs: {} as { [serial: string]: JobExecutionSummaryForThing[] },
    devices: {} as { [serial: string]: Device },
    devicesRequestedForAccount: {} as { [accountKey: string]: boolean },
    firmwares: {} as { [serial: string]: string },
    isCheckingFleetStatus: false,
    isLoadingRavenSettings: false,
    isLoadingSimUsage: false,
    isReturning: false,
    orderDeviceRequests: {} as { [orderNumber: string]: boolean },
    ravenDeviceProvisioned: false,
    ravenFleetError: null as string | null,
    ravenSettings: {} as RavenSettings,
    ravenSettingsError: null as string | null,
    readOnlyConfigs: {} as { [serial: string]: boolean },
    returnSuccessfulClose: '',
    returnError: '',
    returnLastChecked: '',
    returnPrecheckInProgress: false,
    returnResponseError: '',
    returnResponseWarning: '',
    returnSerial: '',
    returnSuccess: '',
    sDCardStatus: null as string | null,
    simStatuses: {} as { [serial: string]: SimDetails['status'] },
    simUsage: {} as { [serial: string]: SimUsage[] },
    simDataLoading: {} as { [serial: string]: boolean },
    simUsageLoading: {} as { [serial: string]: boolean },
    statusesLoading: {} as { [serial: string]: boolean },
  }),
  getters: {
    devicesByAccount(state) {
      const devicesByAccount: { [key: string]: Device[] } = {};
      Object.values(state.devices).forEach((device) => {
        const { accountKey } = device;
        if (accountKey) {
          devicesByAccount[accountKey] = [...(devicesByAccount[accountKey] || []), device];
        }
      });
      return devicesByAccount;
    },
    devicesByKey(state) {
      const devicesByKey: { [key: string]: Device } = {};
      Object.keys(state.devices).forEach((serial) => {
        const device = state.devices[serial];
        if (device.key) {
          devicesByKey[device.key] = device;
        }
      });
      return devicesByKey;
    },
    devicesByOrder(state) {
      const devicesByOrder: { [orderNumber: string]: Device[] } = {};
      Object.keys(state.devices).forEach((serial) => {
        const device = state.devices[serial];
        if (device.orderNumber) {
          const devices = devicesByOrder[device.orderNumber];
          devicesByOrder[device.orderNumber] = [...(devices || []), { ...device }];
        }
      });
      return devicesByOrder;
    },
    devicesLoading(state) {
      const devicesLoading: { [serial: string]: boolean } = {};
      Object.keys(state.devices).forEach((serial) => {
        const device = state.devices[serial];
        if (device.isLoading) {
          devicesLoading[serial] = true;
        }
      });
      return devicesLoading;
    },
  },
  actions: {
    addJobStatus(serial: string, summary: JobExecutionSummaryForThing) {
      this.deviceJobs[serial] = [summary, ...this.deviceJobs[serial]];
    },
    async applyBulkUpdates(serials: string[], updates: BulkUpdatePayload, bulkExecutionId: string) {
      const response = await api.patch<{ error?: string }>('/device/bulk-config-update', {
        bulkExecutionId,
        serials,
        updates,
      });
      const { data } = response;
      return data;
    },
    bulkUpdate(serials: string[], updates: BulkUpdatePayload, bulkExecutionId: string) {
      Dialog.create({
        component: BulkExecutionDialog,
        componentProps: {
          bulkExecutionId,
          serials,
          updates,
        },
      });
    },
    async checkFleetStatus(serial: string) {
      this.ravenFleetError = null;

      const { accounts, currentAccount } = useAccountStore();
      const account = accounts[<string>currentAccount];

      this.isCheckingFleetStatus = true;

      try {
        const { data } = await api.get<boolean>(`/device/${serial}/raven/fleet-check?accountId=${account.id}`);
        this.$patch((state) => {
          state.devices[serial].isInFleet = data;
        });
      } catch (error) {
        const errorResponse = (<AxiosResponse<{ error: string }>>(<AxiosError>error).response)?.data?.error;
        this.ravenFleetError = errorResponse || (<Error>error).message;
      }

      this.isCheckingFleetStatus = false;
    },
    disassociate(serial: string) {
      const device = this.$state.devices[serial];

      this.$patch((state) => {
        state.devices[serial] = {
          ...device,
          error: undefined,
          orderNumber: undefined,
          sku: undefined, // TODO: revert to original SKU or prevent using already associated devices
          replaces: undefined,
        };

        // Undo replacement
        if (device.replaces) {
          state.devices[device.replaces] = {
            ...state.devices[device.replaces],
            error: undefined, // reset error status
            replacedBy: undefined,
          };
        }
      });
    },
    toggleReplaceableDevice(serial: string, orderNumber: string, sku: string) {
      this.$patch((state) => {
        if (!state.devices[serial].willReplace) {
          state.devices[serial].orderNumber = orderNumber;
          state.devices[serial].sku = sku;
          state.devices[serial].willReplace = true;
        } else {
          state.devices[serial].orderNumber = undefined;
          state.devices[serial].sku = undefined;
          state.devices[serial].willReplace = false;
        }
      });
    },
    getDevice(serial: string, refresh = false) {
      return this.getDevices({ serials: [serial], refresh });
    },
    getDevices({ serials, refresh = false }: { serials: string[]; refresh?: boolean }) {
      return Promise.all(
        serials.map(async (serialOrKey) => {
          const { devicesLoading, $state } = this;
          const { devices } = $state;

          if (devicesLoading[serialOrKey]) {
            return devices[serialOrKey].request;
          }

          if (devices[serialOrKey] && !devices[serialOrKey].needsData && !refresh) {
            return devices[serialOrKey];
          }

          if (!devices[serialOrKey]) {
            this.initBareDevice(serialOrKey);
          }

          let device = null;
          try {
            const promise = new Promise<Device>((resolve, reject) => {
              void api
                .get<FlexibleDeviceData>(`/device/${serialOrKey}`)
                .then(({ data }) => {
                  device = this.insertDevice({ device: { ...data, needsData: false }, merge: !refresh });
                  resolve(device);
                })
                .catch(reject);
            });

            this.$state.devices[serialOrKey].isLoading = true;
            this.$state.devices[serialOrKey].request = promise;

            await promise;
          } catch (error) {
            if (axios.isAxiosError(error)) {
              const data = <{ error: string }>error.response?.data;
              device = this.insertDevice({
                device: <FlexibleDeviceData>{ serial: serialOrKey, error: data.error },
              });
            } else {
              console.error('getDevices error:', error);
            }
          }

          this.$patch((state) => {
            state.devices[serialOrKey].isLoading = false;
            delete state.devices[serialOrKey].request;
          });

          return device;
        })
      );
    },
    async getDeviceJobStatus(serial: string) {
      this.commandJobsLoading[serial] = true;

      const { data, status } = await api.get<JobExecutionSummaryForThing[]>(`/device/${serial}/job-status`, {
        validateStatus: validStatuses(STATUSES.NOT_FOUND),
      });

      if (status === 200) {
        this.deviceJobs[serial] = data;
      }

      delete this.commandJobsLoading[serial];
    },
    getDeviceType(device?: Device) {
      const { modelName, serial = '' } = device || {};

      // Determine type by model
      switch (modelName) {
        case 'Raven':
        case 'RVP0A0':
          return DEVICE_TYPE.RAVEN;
        case 'Munic':
        case 'Munin':
        case 'Munic3 2G With MUX':
          return DEVICE_TYPE.MUNIC;
        default:
      }

      // Determine type by serial
      switch (true) {
        case serial.includes('RVP'):
          return DEVICE_TYPE.RAVEN;
        case serial.startsWith('QT') || serial.startsWith('QW'):
          return DEVICE_TYPE.ASSET;
        default:
          DEVICE_TYPE.OTHER;
      }
    },
    async getAccountDevices(accountKey: string, refresh = false) {
      const { accountDeviceRequests, devicesRequestedForAccount } = this;
      const { accountRequests, accounts, inProgress: accountsLoading } = useAccountStore();

      delete this.accountDeviceErrors[accountKey];

      if (Boolean(accountDeviceRequests[accountKey])) {
        return accountDeviceRequests[accountKey];
      }

      if (!refresh && (!accountKey || devicesRequestedForAccount[accountKey])) {
        return;
      }

      if (accountsLoading[accountKey]) {
        await accountRequests[accountKey];
        void this.getAccountDevices(accountKey, refresh);
        return;
      }

      if (accounts[accountKey]?.locked) {
        return;
      }

      try {
        const promise = getAll<{ devices: ApiDevice[]; cursor: string } & { error: string }>(
          `/devices?accountKey=${accountKey}`,
          {},
          async ({ devices, error }, status) => {
            if (status === 200) {
              await Promise.all(
                devices.map((device) =>
                  this.insertDevice({ device: <FlexibleDeviceData>{ ...device, needsData: true } })
                )
              );
            } else if (status !== 403) {
              Bugsnag.notify(error);
            }
          }
        );

        this.$state.accountDeviceRequests = {
          ...this.$state.accountDeviceRequests,
          [accountKey]: promise,
        };

        await promise;
      } catch (error) {
        const logger = useLogger();
        logger.error('getAccountDevices error:', <Error>error);
        const { data } = <AxiosResponse<{ error: string }>>(<AxiosError>error).response;
        this.accountDeviceErrors[accountKey] = data.error;
      }

      this.devicesRequestedForAccount[accountKey] = true;

      this.$patch((state) => {
        delete state.accountDeviceRequests[accountKey];
      });
    },
    async getDeviceCommands(serial: string) {
      delete this.deviceCommands[serial];

      const { data } = await api.get<InternalIoTCommands>(`/device/${serial}/commands`, {
        validateStatus: validStatuses(STATUSES.NOT_FOUND),
      });

      const { available_commands: availableCommands } = data;

      this.deviceCommands[serial] = availableCommands;

      return availableCommands;
    },
    async getDeviceConfiguration(serial: string) {
      const { data } = await api.get<IotDeviceConfigurationData>(`/device/${serial}/config`);

      const { firmware } = data;
      this.firmwares[serial] = firmware;

      return data;
    },
    async getDeviceConfigurationStatus(serial: string) {
      this.statusesLoading[serial] = true;

      const { data } = await api.get<IotDeviceConfigurationStatusDelta & ApiErrorPayload>(
        `/device/${serial}/config/status`,
        {
          validateStatus: validStatuses(STATUSES.NOT_FOUND),
        }
      );

      if (!_isEmpty(data)) {
        if (data.error) {
          this.configStatuses[serial] = 'Unknown';
        } else {
          this.configStatuses[serial] = 'Pending';
        }
      } else {
        this.configStatuses[serial] = 'Set';
      }

      this.configStatusDeltas[serial] = _isEmpty(data) ? null : <IotDeviceConfigurationStatusDelta>data;

      delete this.statusesLoading[serial];

      return data;
    },
    async getDeviceConnectivity(serial: string) {
      const { dayjs } = useDayjs();

      this.connectivityLoading[serial] = true;

      const { data } = await api.get<ThingDocument & ApiErrorPayload>(`/device/${serial}/connectivity`, {
        validateStatus: validStatuses(STATUSES.NOT_FOUND),
      });

      const connectivityStatus = this.statusToValue(data);
      const connectivityTimestamp = data.connectivity?.timestamp ? dayjs(data.connectivity.timestamp) : null;

      this.connectivityStatuses[serial] = connectivityStatus;

      this.connectivityLoading[serial] = false;

      return {
        connectivity: data.connectivity,
        connectivityStatus,
        connectivityTimestamp,
      };
    },
    async getSim({ serial, sim, source }: { serial?: string; sim?: string; source?: string }) {
      if (!serial) {
        return {
          data: {
            status: 'unknown',
          },
        };
      }

      this.simDataLoading[serial] = true;

      const query = QueryString.stringify({
        sim,
        source,
      });

      let simData = { data: {} } as { data: SimDetails | ApiErrorPayload };
      try {
        simData = await api.get<SimDetails | ApiErrorPayload>(`/device/${serial}/sim${query ? `?${query}` : ''}`);
      } catch (error) {
        simData.data.status = 'unknown';
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          simData.data.status = 'not found';
        }
      }

      this.simStatuses[serial] = <SimDetails['status']>simData.data.status;

      delete this.simDataLoading[serial];

      return simData;
    },
    async getSimUsage({ serial, sim, source }: { serial?: string; sim?: string; source?: string }) {
      let simUsage = [] as SimUsage[];

      if (!serial) {
        return [];
      }

      this.simUsageLoading[serial] = true;

      const query = QueryString.stringify({
        sim,
        source,
      });

      let data;
      try {
        const response = await api.get<SimUsage[] & ApiErrorPayload>(
          `/device/${serial}/sim/usage${query ? `?${query}` : ''}`
        );
        data = response.data;
        simUsage = _sortBy(data, 'date');
      } catch {
        // do nothing
      }

      this.simUsage[serial] = simUsage;

      delete this.simUsageLoading[serial];

      return simUsage;
    },
    getModelAlias(modelName?: string): string {
      if (!modelName) {
        return '';
      }
      return DEVICE_MODEL_ALIASES[modelName] || modelName;
    },
    async getOrderDevices(orderNumber: string) {
      const { orders } = storeToRefs(useOrderStore());

      const { orderDeviceRequests } = this;

      if (orderDeviceRequests[orderNumber]) {
        return;
      }

      const skus = orders.value[orderNumber]?.items?.map(({ sku }) => sku);

      if (_isEmpty(skus)) {
        return;
      }

      this.orderDeviceRequests[orderNumber] = true;

      try {
        const response = await api.get<FlexibleDeviceData[]>(
          `/orders/${orderNumber}/devices?skus=${(skus || []).join(',')}`
        );
        response.data.map((device) =>
          this.insertDevice({
            device: {
              ...device,
              isNew: false,
            },
          })
        );
      } catch (error) {
        const logger = useLogger();
        logger.error('getOrderDevices error:', <Error>error);
      }

      delete this.orderDeviceRequests[orderNumber];
    },
    async getRavenSettings(serial: string, uuid?: string) {
      this.isLoadingRavenSettings = true;

      const uuidString = uuid ? `?uuid=${uuid}` : '';

      try {
        const { data } = await api.get<RavenApiDeviceSettings>(`/device/${serial}/raven/settings${uuidString}`);
        this.setRavenSettings(data);
      } catch (error) {
        const logger = useLogger();
        logger.error('Failed to retrieve Raven settings', <Error>error);
        this.ravenSettingsError = 'Failed to retrieve Raven settings.';
      }

      this.isLoadingRavenSettings = false;

      return this.ravenSettings;
    },
    initBareDevice(serial: string) {
      this.$state.devices[serial] = {
        isLoading: false,
        isNew: true,
        needsData: true,
        serial,
      };
    },
    insertDevice({ device: deviceData, merge = true }: { device: FlexibleDeviceData; merge?: boolean }) {
      const device = <Device>_omitBy(
        <Device>{
          accountKey: deviceData.account_key,
          batteryChargeLevel: deviceData.battery_charge_level,
          configName: deviceData.config_name,
          created: deviceData.created,
          customerStatus: deviceData.customer_status || deviceData.status,
          error: deviceData.error,
          fallbackVin: deviceData.fallback_vin,
          imei: deviceData.imei,
          inventoryStatus: deviceData.inventory_status,
          isAsset: deviceData.is_asset,
          isFulfilled: deviceData.is_fulfilled,
          isLoading: deviceData.isLoading,
          isNew: deviceData.isNew,
          isPending: deviceData.is_pending,
          isRaven: deviceData.is_raven,
          key: deviceData.key,
          lastTransmission: deviceData.last_transmission,
          modelName: deviceData.model_name || deviceData.model,
          needsData: deviceData.needsData !== undefined ? deviceData.needsData : undefined,
          orderNumber: deviceData.order_number,
          replacedBy: deviceData.replaced_by,
          replaces: deviceData.replaces,
          ravenFleetId: deviceData.raven_fleet_id,
          ravenOnline: deviceData.raven_online,
          ravenOsVersion: deviceData.raven_os_version,
          ravenSdCardStatus: deviceData.raven_sd_card_status,
          ravenStatus: deviceData.raven_status,
          ravenUnplugged: deviceData.raven_unplugged,
          ravenUuid: deviceData.raven_uuid,
          reportedObdFirmware: deviceData.reported_obd_firmware,
          serial: deviceData.serial,
          sim: deviceData.sim,
          simActive: deviceData.sim_active,
          simSource: deviceData.sim_source,
          sku: deviceData.sku,
          vehicleKey: deviceData.vehicle_key,
          willReplace: deviceData.will_replace,
        },
        _isNil
      );

      // Merge existing data (look for device entries that were added by key)
      const existingData = merge ? this.$state.devices[device.serial] || this.$state.devices[<string>device.key] : {};

      if (merge) {
        // Do not flag as "needs data" if it doesn't currently need data
        if ((<Device>existingData)?.needsData === false) {
          device.needsData = false;
        }
      }

      this.$state.devices[device.serial] = { ...existingData, ...device };

      // Handle requests that were made using device key
      if (this.$state.devices[<string>device.key]) {
        // Point it to the record saved under device serial
        this.$state.devices[<string>device.key] = this.$state.devices[device.serial];
      }

      return device;
    },
    markAsClaimed(serials: string[]) {
      this.$patch((state) => {
        serials.forEach((serial) => {
          state.devices[serial].isNew = false;
        });
      });
    },
    markAsReplaced(serial: string) {
      this.$patch((state) => {
        state.devices[serial].accountKey = undefined;
        state.devices[<string>this.devices[serial].replacedBy].isNew = false;
      });
    },
    async provisionRaven(serial: string, accountId: string) {
      const result = {} as { success: boolean; error?: string };

      try {
        const { data } = await api.post<RavenApiProvisionedDevice & { account_key: string; history_id: number }>(
          `/device/${serial}/raven/provision?accountId=${accountId}`
        );

        // Watch for a different status than "ASSIGNED" (just in case)
        if (data.raven.status !== 'ASSIGNED') {
          const { error } = useLogger();
          error(`Raven provisioning call returned with an unexpected status: ${data.raven.status}`);
        }

        result.success = true;

        // Update device to indicate it is now in the fleet
        this.$patch((state) => {
          state.devices[serial].isInFleet = true;
        });

        showChangeActionDialog('Raven camera successfully provisioned.', {
          accountKey: data.account_key,
          serial,
          historyId: data.history_id,
          icon: 'videocam',
        });
      } catch (error) {
        result.success = false;
        result.error = (<Error>error).message;
      }

      return result;
    },
    async returnDevice(serial: string) {
      const historyStore = useHistoryStore();
      const device = this.devices[serial];
      let result = {} as { device: FlexibleDeviceData, error?: string; history: HistoryResult; closedSubscription?: ApiSubscription | { infoMessage: string; isError: boolean } };

      // TODO: move all return stuff into its own store
      this.$state.isReturning = true;
      this.$state.returnResponseError = '';
      this.$state.returnResponseWarning = '';
      this.$state.returnSuccess = '';
      this.$state.returnSuccessfulClose = '';

      try {
        const accountKeyParam = device.accountKey ? `?accountKey=${device.accountKey}` : '';
        const { data } = await api.post<typeof result>(
          `/devices/return${accountKeyParam}`,
          {
            serial: device.serial,
          }
        );
        result = data;
        this.$patch((state) => {
          delete state.devices[serial];
        });
        this.insertDevice({ device: data.device });

        historyStore.insertReturn(data.history);

        this.$state.returnSerial = '';
        this.$state.returnSuccess = `Device #${serial} terminated`;

        const nonClosedSubscription = <{ infoMessage: string; isError: boolean }>result.closedSubscription || {};
        if (nonClosedSubscription.infoMessage) {
          this.$state[nonClosedSubscription.isError ? 'returnResponseError' : 'returnResponseWarning'] = nonClosedSubscription.infoMessage;
        }

        const closedSubscription = <ApiSubscription>result.closedSubscription || {};
        if (closedSubscription.id) {
          this.$state.returnSuccessfulClose = `Closed subscription #${closedSubscription.id} (no recurring devices remaining)`;
        }
      } catch (error) {
        const { data } = <AxiosResponse<{ error: string }>>(<AxiosError>error).response || {};
        this.$state.returnResponseError = data?.error || (<Error>error).message;
      }

      this.$state.isReturning = false;

      return result;
    },
    async saveRavenSettings(serial: string, uuid: string, settings: RavenSettings) {
      this.isLoadingRavenSettings = true;

      const apiSettings = settingsToRaven(settings);

      try {
        const { data } = await api.patch<RavenApiDeviceSettings & { history_id: number }>(
          `/device/${serial}/raven/settings?uuid=${uuid}`,
          apiSettings
        );
        this.ravenSettings = { ...settings };

        showChangeActionDialog(`Raven options updated for device #${serial}`, {
          historyId: data.history_id,
          serial,
          timeout: 10000,
        });
      } catch (error) {
        const logger = useLogger();
        logger.error('Failed to save Raven settings', <Error>error);
        this.ravenSettingsError = 'Failed to save Raven settings.';
      }

      this.isLoadingRavenSettings = false;

      return this.ravenSettings;
    },
    async sendDeviceCommand(serial: string, command: string) {
      const response = await api.post<IotDeviceConfigurationData & { history_id: number }>(
        `/device/${serial}/commands`,
        { command }
      );

      const { data } = response;

      showChangeActionDialog(`${command} sent to device #${serial}.`, {
        historyId: data.history_id,
        serial,
        timeout: 10000,
      });

      return response;
    },
    async setDeviceConfiguration(serial: string, config: string, showDialog = true) {
      let data = {} as IotDeviceConfigurationData & { history_id: number };

      this.applyingConfigs[serial] = true;
      this.configStatusDeltas[serial] = null;

      try {
        const response = await api.put<IotDeviceConfigurationData & { history_id: number }>(
          `/device/${serial}/config`,
          {
            config,
          }
        );

        data = response.data;

        const { firmware } = data;

        this.firmwares[serial] = firmware;
        this.configStatuses[serial] = 'Pending';

        if (showDialog) {
          showChangeActionDialog(`Configuration changed to ${config}.`, {
            historyId: data.history_id,
            serial,
            timeout: 10000,
          });
        }
      } finally {
        delete this.applyingConfigs[serial];
      }

      return data;
    },
    setDeviceError({ error, relatedSerial, serial }: { error: string; relatedSerial?: string; serial: string }) {
      this.insertDevice({ device: <FlexibleDeviceData>{ serial, error } });
      if (relatedSerial) {
        this.insertDevice({ device: <FlexibleDeviceData>{ serial: relatedSerial, error } });
      }
    },
    async setFallbackVin(serial: string, vin: string | null) {
      const response = await api.patch<ApiDevice & { history_id: number }>(`/device/${serial}`, {
        fallback_vin: vin,
      });

      const { data } = response;

      const action = vin === null ? 'removed from' : `(${vin}) set for`;

      showChangeActionDialog(`Fallback VIN ${action} device #${serial}.`, {
        historyId: data.history_id,
        serial,
        timeout: 10000,
      });

      return response;
    },
    setRavenSettings(settings: RavenApiDeviceSettings) {
      this.ravenSettings = ravenToSettings(settings);
    },
    setReadOnlyConfig(serial: string) {
      this.readOnlyConfigs[serial] = true;
    },
    statusToValue(device: ThingDocument & ApiErrorPayload): DeviceConnectivityStatus {
      if (!device.connectivity) {
        if (device.error === 'Device not found') {
          return device.error;
        }
        return 'Unknown';
      }

      return device.connectivity.connected ? 'Connected' : 'Disconnected';
    },
    async swap(
      serial: string,
      replacementSerial: string,
      accountKey: string,
      { upsTrackingNumber, shipDate }: { upsTrackingNumber: string; shipDate: string }
    ) {
      const result = {} as ApiSimplePayload;

      try {
        const { data } = await api.delete<ApiSimplePayload & { device: FlexibleDeviceData }>(
          `/device/${serial}?accountKey=${accountKey}&replacement_serial=${replacementSerial}&upsTrackingNumber=${upsTrackingNumber}&shipDate=${shipDate}`
        );
        result.success = true;
        result.history_id = data.history_id;
        this.$patch((state) => {
          delete state.devices[serial];
        });
        this.insertDevice({ device: data.device });
      } catch (error) {
        const { data } = <AxiosResponse<{ error: string }>>(<AxiosError>error).response;
        result.success = false;
        result.error = data?.error || (<Error>error).message;
      }

      if (result.success) {
        showChangeActionDialog(`Device ${serial} has been removed and ${replacementSerial} has been claimed.`, {
          accountKey,
          historyId: <number>result.history_id,
          icon: 'swap_horiz',
          serial: replacementSerial, // comments will appear for the replacement device
        });
      }

      return result;
    },
    async terminate(serial: string, accountKey: string) {
      const result = {} as ApiSimplePayload;

      try {
        const { data } = await api.delete<ApiSimplePayload>(`/device/${serial}?accountKey=${accountKey}`);
        result.success = true;
        result.history_id = data.history_id;
        this.$patch((state) => {
          delete state.devices[serial];
        });
      } catch (error) {
        const { data } = <AxiosResponse<{ error: string }>>(<AxiosError>error).response;
        result.success = false;
        result.error = data?.error || (<Error>error).message;
      }

      if (result.success) {
        showChangeActionDialog(`Device #${serial} terminated.`, {
          accountKey,
          historyId: <number>result.history_id,
          icon: 'delete',
          serial,
          forceType: 'account',
        });
      }

      return result;
    },
    unsetReadOnlyConfig(serial: string) {
      delete this.readOnlyConfigs[serial];
    },
  },
});
